/**
 * Lift2Fit — Privacy Policy (v1).
 *
 * Written to describe what the code actually does. Where the product has a
 * capability that would change the answer (a new processor, a new cross-user
 * use), the wording says what CATEGORY would be used and on what terms, so it
 * stays true when that capability is added rather than promising "never".
 */
import { LegalCallout, LegalList, LegalP, LegalSection, LegalTable } from './LegalLayout';
import { CONTACT_DPO, CONTACT_PRIVACY, DOMAIN, POLICY_VERSION, effectiveDateLabel } from './policy';

export function PrivacyPolicy() {
  return (
    <>
      <LegalCallout title="The short version">
        <LegalP>
          Lift2Fit is a training log and coaching tool, not a healthcare provider. Nothing in it is medical
          advice, and our{' '}
          <a className="font-bold text-accent hover:underline" href="#/legal/health-disclaimer">
            Health &amp; Medical Disclaimer
          </a>{' '}
          says so in full. We collect what we need to run your account and coach your training, including
          health-implying data such as pain and injury notes. We use your data to run the service, to improve it,
          and to train and evaluate our own coaching models — and because a model learns from many accounts, that
          improvement can be a use across users, which we say plainly in the section on internal improvement and
          AI. We do not sell your data and we do not share it for other companies’ marketing. This page is the
          detail behind that summary.
        </LegalP>
      </LegalCallout>

      <LegalSection id="who-we-are" title="1. Who we are and what this covers">
        <LegalP>
          Lift2Fit ({DOMAIN}) provides a training log, a programme builder and automated coaching suggestions, plus
          a workspace for coaches and gyms. This Privacy Policy explains how we handle personal data when you use
          the Lift2Fit web app, our mobile and watch apps, and this website.
        </LegalP>
        <LegalP>
          For the purposes of the UK GDPR and the EU GDPR, Lift2Fit is the <strong>controller</strong> of the
          personal data described here. A coach or gym using the workspace may be a separate controller of the
          client data they hold — see section 8.
        </LegalP>
        <LegalP>
          This policy is written to be read alongside our{' '}
          <a className="font-bold text-accent hover:underline" href="#/legal/terms">
            Terms of Service
          </a>{' '}
          and our{' '}
          <a className="font-bold text-accent hover:underline" href="#/legal/health-disclaimer">
            Health &amp; Medical Disclaimer
          </a>
          . It is version {POLICY_VERSION} and takes effect on {effectiveDateLabel()}.
        </LegalP>
      </LegalSection>

      <LegalSection id="what-we-collect" title="2. What we collect">
        <LegalList
          items={[
            <>
              <strong>Account data.</strong> Your email address, display name, password (stored only as a salted
              scrypt hash), timezone, unit preference, roles and plan.
            </>,
            <>
              <strong>Training data.</strong> The programmes you or your coach build, the sessions you start, the
              sets you log (weight, reps, RPE, rest, tempo), and your personal records and streaks.
            </>,
            <>
              <strong>Health-implying data.</strong> Bodyweight and height, birth year, injury flags, pain scores,
              mood and wellness check-ins, and any notes you write. Some of this is special category data — see
              section 5.
            </>,
            <>
              <strong>Coaching data.</strong> If you link a coach: the link itself, the fields you choose to share
              (sessions, sets, bodyweight, notes, pain), and messages between you.
            </>,
            <>
              <strong>Consent records.</strong> That you accepted this policy, the policy version, the time, and
              the request’s IP address and browser User-Agent. See section 6.
            </>,
            <>
              <strong>Technical data.</strong> The minimum needed to serve the app: the address your request came
              from and the session tokens that keep you signed in.
            </>,
          ]}
        />
        <LegalP>
          We do not ask for your real name, address, date of birth or payment card details to use the free tier,
          and we do not collect special category data such as ethnicity, religion, sexual orientation or biometric
          identifiers.
        </LegalP>
      </LegalSection>

      <LegalSection id="why-and-basis" title="3. Why we use it, and our lawful basis">
        <LegalTable
          head={['Purpose', 'Data used', 'Lawful basis']}
          rows={[
            {
              cells: [
                'Create and run your account; provide the tracker, programme builder and coaching suggestions',
                'Account, training and health-implying data',
                'Performance of a contract (UK/EU GDPR Art. 6(1)(b))',
              ],
            },
            {
              cells: [
                'Keep the service secure, prevent abuse, and diagnose faults',
                'Account, session and technical data',
                'Legitimate interests (Art. 6(1)(f)) — a safe, working service',
              ],
            },
            {
              cells: [
                'Improve the service and train and evaluate our own coaching models',
                'Training, programme and usage data, including health-implying data',
                'Consent (Art. 6(1)(a)); explicit consent for special category data (Art. 9(2)(a))',
              ],
            },
            {
              cells: [
                'Record that you accepted this policy, and defend that record if it is challenged',
                'Consent record, timestamp, IP address and User-Agent',
                'Legal obligation (Art. 6(1)(c)) and legitimate interests in evidencing consent (Art. 6(1)(f))',
              ],
            },
            {
              cells: [
                'Send service messages (security, material changes to the service or this policy)',
                'Email address',
                'Performance of a contract and legal obligation',
              ],
            },
            {
              cells: [
                'Send marketing emails',
                'Email address',
                'Separate consent (Art. 6(1)(a)). We do not send marketing today; if we ever do, it will be opt-in and separate from this policy',
              ],
            },
          ]}
        />
        <LegalP>
          Where we rely on <strong>legitimate interests</strong>, we have balanced our interest against your
          rights and freedoms; the interest is running a secure service that works. You can object to that
          processing — see section 11. Where we rely on <strong>consent</strong>, you can withdraw it at any time
          without affecting the lawfulness of what we did before you withdrew.
        </LegalP>
      </LegalSection>

      <LegalSection id="health-data" title="4. Health and special category data">
        <LegalCallout tone="warning" title="This matters">
          <LegalP>
            Pain scores, injury flags, bodyweight, wellness check-ins and free-text notes can reveal information
            about your health. Under the UK GDPR and EU GDPR that is <strong>special category data</strong>, which
            needs a stronger legal basis than ordinary personal data.
          </LegalP>
        </LegalCallout>
        <LegalP>
          We rely on your <strong>explicit consent</strong> (Art. 9(2)(a)) to process health-implying data for the
          core coaching features and to improve our models. You give that consent by ticking the signup checkbox
          and by choosing to enter that data; you can withdraw it at any time, but if you do we may not be able to
          provide the coaching features that depend on it (for example, load suggestions that account for an
          injury flag).
        </LegalP>
        <LegalP>
          We do not use health-implying data for advertising and we do not sell it. We do not show it to other
          users. If you link a coach, the sharing controls in section 6 govern what health-related information
          they can see; please review those controls before you link, because pain scores and notes can appear
          inside the sessions you share.
        </LegalP>
      </LegalSection>

      <LegalSection id="internal-improvement" title="5. Internal improvement and AI — including use across users">
        <LegalCallout tone="warning" title="The use the signup checkbox is really about">
          <LegalP>
            Your logged training may be used to improve Lift2Fit and to <strong>train and evaluate our own AI
            models</strong>, and those models generate suggestions. Because a model learns from many accounts, a
            pattern learned from your data can improve a suggestion shown to another lifter, and their data can
            improve a suggestion shown to you. That is a <strong>cross-user use</strong>, and we are telling you
            about it rather than burying it.
          </LegalP>
        </LegalCallout>
        <LegalP>
          What crosses between accounts is <strong>patterns the model has learned</strong>, not your name, email
          address or identifiable records. We do not display one lifter’s training records to another lifter. Any
          example, benchmark or aggregate we show is built so that it does not identify an individual.
        </LegalP>
        <LegalP>
          The coaching suggestions in this deployment are produced by our own software running inside our service.
          We currently do not send your personal data to an external AI provider. If we add one, it will act as our
          processor under a written contract, only on our documented instructions, and we will name the category
          here before it is used for your data.
        </LegalP>
        <LegalP>
          <strong>Your choice.</strong> Consent to this improvement use is given at signup and can be withdrawn at
          any time by contacting {CONTACT_PRIVACY}. Withdrawing it stops your data being used to train new models,
          but does not unwind models already trained; we will not use your identifiable data in future training
          runs after your request is processed.
        </LegalP>
      </LegalSection>

      <LegalSection id="sharing" title="6. What we do not do, and the sharing that does happen">
        <LegalP>
          <strong>We do not sell your personal data.</strong> We do not share it with third parties for their own
          advertising or marketing, and we do not operate advertising trackers.
        </LegalP>
        <LegalP>
          <strong>We do not make one lifter’s personal data visible to another lifter.</strong> A coach sees a
          client’s data only through a coaching relationship the client accepts, and only the fields the client
          shares. The limited sharing that does happen is:
        </LegalP>
        <LegalList
          items={[
            <>
              <strong>With your coach, if you link one.</strong> Linking a coach lets them see the training you
              record. The sharing controls let you choose which categories a coach can see — sessions, sets,
              bodyweight, notes and pain — with sessions and sets on by default, and you can change the selection
              or revoke the link at any time on your Manage coach page. Because pain scores and free-text notes can
              appear inside a session record, do not log anything you are not prepared to share with a coach while
              a link is active.
            </>,
            <>
              <strong>With a coach whose client you are, if they hold the data.</strong> A coach is a separate
              controller of what they hold — see section 8.
            </>,
            <>
              <strong>With our service providers.</strong> The two external requests described in section 7
              (Google Fonts, and OpenStreetMap only when you click “View on map”), and any future processor acting
              only on our instructions under contract.
            </>,
            <>
              <strong>Where the law requires it.</strong> If we are legally compelled to disclose data, we will
              disclose only what is required and, where we are allowed to, tell you.
            </>,
            <>
              <strong>If the business changes hands.</strong> If Lift2Fit is sold or merged, data may transfer as
              part of that transaction; this policy would continue to apply, and we would tell you before your data
              became subject to a different one.
            </>,
          ]}
        />
        <LegalP>
          Lift2Fit’s administrators can access account records to operate and support the service. That access is
          limited to staff who need it, and administrative changes are recorded in an audit trail.
        </LegalP>
      </LegalSection>

      <LegalSection id="processors" title="7. Service providers and international transfers">
        <LegalP>
          Today, two external providers receive data from the web app. Neither is used for advertising or
          profiling, and neither receives your training records:
        </LegalP>
        <LegalList
          items={[
            <>
              <strong>Google Fonts (fonts.googleapis.com, fonts.gstatic.com).</strong> The web page loads its
              typefaces from Google’s font CDN, so Google receives the request itself — including your IP address
              and browser User-Agent. We are moving to self-hosted fonts so that this transfer is removed; until
              then it is the one automatic third-party request the app makes.
            </>,
            <>
              <strong>OpenStreetMap (www.openstreetmap.org).</strong> Only when you choose “View on map” for a
              coach’s location, your browser opens OpenStreetMap with the address in the URL. No request is made
              unless you click.
            </>,
          ]}
        />
        <LegalCallout tone="warning" title="Why we are telling you this">
          <LegalP>
            “We share nothing with anyone” would be a promise this deployment cannot keep: the font request above
            is a real transfer of your IP address to Google. We would rather state the two transfers that happen
            than publish an absolute that a future email provider would break.
          </LegalP>
        </LegalCallout>
        <LegalP>
          If we add a processor — for example managed hosting, email delivery, error monitoring, or an external AI
          provider — it will process personal data only on our documented instructions and under a written
          contract that includes confidentiality and security obligations. We will name the category here before
          your data is used with it. We do not use processors for advertising or data brokerage.
        </LegalP>
        <LegalP>
          Google’s font CDN is operated globally and may process a request outside the UK or the European Economic
          Area. Where a provider processes data outside those areas we put appropriate safeguards in place first —
          an adequacy decision, or the standard contractual clauses with a transfer risk assessment. You can ask
          us for the mechanism used for a particular transfer at {CONTACT_PRIVACY}.
        </LegalP>
        <LegalP>
          The AI coaching in this deployment runs inside our own service. No external AI provider is configured or
          called, so using the coach does not by itself send your data to a third country.
        </LegalP>
      </LegalSection>

      <LegalSection id="coaches" title="8. Coaches and coaching relationships">
        <LegalP>
          If you use the Lift2Fit coach or gym workspace, you are a <strong>separate controller</strong> of the
          personal data you hold about your clients. You are responsible for having your own lawful basis to
          collect it, for giving your clients your own privacy notice, for responding to their rights requests,
          and for keeping it secure. Lift2Fit is a processor for the data we store on your behalf in the
          workspace; we act on your instructions and on the terms of our agreement with you.
        </LegalP>
        <LegalP>
          Coaches on the platform are independent. We do not employ them, we do not supervise the programmes they
          write, and a coach’s programme is theirs, not our advice. Section 4 of our{' '}
          <a className="font-bold text-accent hover:underline" href="#/legal/terms">
            Terms of Service
          </a>{' '}
          sets out the relationship in full.
        </LegalP>
        <LegalP>
          If you are a coach, you must not use Lift2Fit to process client data for any purpose beyond delivering
          coaching through the service, and you must not attempt to re-identify data that has been made
          non-identifying.
        </LegalP>
      </LegalSection>

      <LegalSection id="retention" title="9. How long we keep it">
        <LegalTable
          head={['What', 'How long', 'Why']}
          rows={[
            {
              cells: [
                'Account and profile data',
                'While your account is open, then deleted within 30 days of closure',
                'To provide the service, then to honour erasure',
              ],
            },
            {
              cells: [
                'Training and health-implying logs',
                'While your account is open; deleted with the account',
                'To provide the service and your history',
              ],
            },
            {
              cells: [
                'Consent record, including the IP address and User-Agent captured at acceptance',
                'For the life of the account and for a period after closure (currently six years)',
                'To evidence that consent was given and to answer a legal challenge',
              ],
            },
            {
              cells: [
                'Sign-in sessions and refresh tokens',
                'Up to 30 days, then expired or revoked',
                'To keep you signed in securely',
              ],
            },
            {
              cells: [
                'Drafts and pending writes on your device',
                'Until they sync or you clear your browser storage',
                'To avoid losing a set when the connection drops',
              ],
            },
          ]}
        />
        <LegalP>
          This deployment runs a single database. If we add a hosted backup, a deleted record may remain in that
          backup for a short period (up to 35 days) before it ages out, and it will not be used for any other
          purpose. The six-year consent-retention period is our current practice and is one of the points we will
          review with a data-protection lawyer.
        </LegalP>
      </LegalSection>

      <LegalSection id="security" title="10. How we protect it">
        <LegalP>
          Passwords are stored only as salted scrypt hashes. Access tokens are short-lived, refresh tokens are
          stored hashed, and every record is scoped to its owner by database row-level security. Production
          traffic is served over TLS. We limit administrator access to the people who need it, and administrative
          changes are recorded in an audit trail.
        </LegalP>
        <LegalP>
          No system is perfectly secure. If a breach affects your rights and freedoms, we will notify you and the
          relevant supervisory authority where the law requires it.
        </LegalP>
      </LegalSection>

      <LegalSection id="rights" title="11. Your rights">
        <LegalP>Under the UK GDPR and EU GDPR you have the right to:</LegalP>
        <LegalList
          items={[
            <>
              <strong>Access</strong> — a copy of the personal data we hold about you, and information about how
              we use it. Your account’s own pages and the JSON export on your profile are a starting point.
            </>,
            <>
              <strong>Rectification</strong> — correct data that is wrong or incomplete. Most of it you can edit
              yourself in your profile.
            </>,
            <>
              <strong>Erasure</strong> — have your data deleted, subject to our legal obligations.
            </>,
            <>
              <strong>Restriction</strong> — ask us to pause using your data while a dispute is resolved.
            </>,
            <>
              <strong>Portability</strong> — receive data you gave us in a structured, machine-readable format.
            </>,
            <>
              <strong>Object</strong> — object to processing based on legitimate interests.
            </>,
            <>
              <strong>Withdraw consent</strong> — at any time, without affecting what we lawfully did before.
            </>,
            <>
              <strong>Complain</strong> — to your local supervisory authority. In the UK that is the Information
              Commissioner’s Office (ico.org.uk); in the EU it is the authority where you live or work. We would
              rather you came to us first, but you do not have to.
            </>,
          ]}
        />
        <LegalP>
          To exercise any of these rights, email {CONTACT_PRIVACY} or our data protection contact at{' '}
          {CONTACT_DPO}. We will respond within one month. We may ask you to confirm your identity first; we will
          not ask for more than we need to do that.
        </LegalP>
      </LegalSection>

      <LegalSection id="cookies" title="12. Cookies and local storage">
        <LegalP>
          We do not set advertising or analytics cookies. The web app stores a small amount of data in your
          browser’s local storage so it can work:
        </LegalP>
        <LegalTable
          head={['Stored locally', 'What it holds']}
          rows={[
            { cells: ['l2f.session', 'Your signed-in session and tokens, so a refresh keeps you signed in'] },
            { cells: ['l2f.theme', 'Your light or dark theme choice'] },
            { cells: ['l2f.unit', 'Your metric or imperial preference'] },
            { cells: ['l2f.grid.v2, l2f.plans', 'In-progress log rows and draft plans, so work is not lost'] },
            { cells: ['l2f.api.base, l2f.api.lan', 'The API address that last answered, so the app reconnects'] },
            { cells: ['l2f.api.pending.v1', 'Sets waiting to sync after a dropped connection'] },
            { cells: ['l2f.runtime.muted', 'Whether workout sounds are muted'] },
          ]}
        />
        <LegalP>
          You can clear all of it from your browser settings; clearing your session storage signs you out. Because
          these are strictly necessary to provide the service you asked for, they are not subject to a consent
          banner.
        </LegalP>
      </LegalSection>

      <LegalSection id="children" title="13. Children">
        <LegalP>
          Lift2Fit is for people aged <strong>16 and over</strong>. We do not knowingly create accounts for
          anyone under 16, and we do not offer a parental-consent flow. If we learn that a child under 16 has
          created an account, we will close it and delete the data. If you believe a child has signed up, contact{' '}
          {CONTACT_PRIVACY}.
        </LegalP>
      </LegalSection>

      <LegalSection id="changes" title="14. Changes to this policy">
        <LegalP>
          We may update this policy. When we make a material change we will increase the version number, change
          the effective date, and tell you before the change takes effect. For a material change we will ask you
          to accept the new version rather than treating your continued use as agreement.
        </LegalP>
        <LegalP>
          Your acceptance is recorded against a specific version. A change does not rewrite the record of what you
          originally accepted — the old row stays, and a new acceptance adds a new row. You can see your recorded
          acceptances on your profile.
        </LegalP>
      </LegalSection>

      <LegalSection id="contact" title="15. Contact">
        <LegalP>
          Privacy questions and rights requests: <strong>{CONTACT_PRIVACY}</strong>. Data protection contact:{' '}
          <strong>{CONTACT_DPO}</strong>. General support: support@{DOMAIN}.
        </LegalP>
        <LegalP>
          Lift2Fit · {DOMAIN}. One stop for all your fitness needs.
        </LegalP>
      </LegalSection>
    </>
  );
}
